import {
  ConversionUtils,
  DeviceTypeHelper,
  Lorax,
  MeterReaderDeviceTypes,
  TR4,
  TR4Info,
  TR4InfoMessageType,
} from '@ncss/models';
import { TR4Read } from '@ncss/models/dist/v3';
import { EndDeviceMessage } from '../messages/endDeviceMessage';
import { DirectConnectEndDevice } from './base-end-device';
import { shallowRef, ShallowRef } from 'vue';
import { EndDeviceInfoMessage } from '../messages/endDeviceInfoMessage';
import { OverallStatus } from '../../base/utils';

export class DirectConnectTR4I extends DirectConnectEndDevice {
  get firmwareVersionStr() {
    if (this._tr4i.value) {
      return this._tr4i.value?.firmwareVersionMajor + '.' + this._tr4i.value?.firmwareVersionMinor;
    }
    return '';
  }
  get hardwareVersionStr() {
    if (this._tr4i.value) {
      return this._tr4i.value.hardwareVersionMajor + '.' + this._tr4i.value.hardwareVersionMinor;
    }
    return '';
  }
  /**
   * The read provided in the most recent message from the TR4-I.
   */
  get meterRead() {
    return this._meterRead.value;
  }
  /**
   * The TR4-I device object provided in the most recent message.
   */
  get tr4i() {
    return this._tr4i.value;
  }
  /**
   * A computed status object for the TR4-I.
   * The status is computed based on the signal strength and whether the TR4-I has received a response from the gateway.
   *
   * The status object contains the following properties:
   * - isOk: A boolean indicating whether the TR4-I is in a good state.
   * - label: A string indicating the status of the TR4-I.
   * - description: A string describing the status of the TR4-I.
   */
  get status() {
    return this._status.value;
  }
  get rapidCheckInEnabled() {
    return !!this._tr4i.value?.rapidCheckInEnabled;
  }
  get temperatureStr() {
    if (!this._tr4i.value) {
      return undefined;
    }
    if (this._useMetric) {
      return this._tr4i.value.temperature + ' C';
    }
    return (ConversionUtils.ConvertCelsiusToFahrenheit(this._tr4i.value.temperature)?.toFixed(1) || 0) + ' F';
  }
  get batteryLevelRemaining() {
    if (!this._tr4i.value) {
      return undefined;
    }
    return Lorax.FormatBatteryYearsRemaining(this._tr4i.value.batteryLevel);
  }

  private _tr4i: ShallowRef<TR4 | undefined> = shallowRef();
  private _meterRead: ShallowRef<TR4Read | undefined> = shallowRef();
  private _status: ShallowRef<OverallStatus | undefined> = shallowRef();

  constructor(msg: EndDeviceMessage, useMetric: boolean) {
    super(msg, useMetric);
    this.updateFromEndDeviceMessage(msg);
  }

  updateFromEndDeviceMessage(msg: EndDeviceMessage): void {
    console.log('%c ON TR4-I EndDeviceMessage', 'color: red', msg);
    if (msg.device instanceof TR4) {
      this._tr4i.value = msg.device;
      this._lastMsgAt.value = new Date();
      this._firstHopRssi.value = msg.firstHopRssi;
      const reads = TR4Read.Create(msg.srcAddr, msg.device, {
        firstHopId: 0,
        firstHopRssi: 0,
        lastHopRssi: 0,
        isProgrammed: false,
      });
      this._meterRead.value = reads[0];
      this._status.value = this.computeOverallStatus(msg.device);
    }
  }

  getSignalTypeMsg(mode: number) {
    const msg = new EndDeviceInfoMessage(this.deviceId, TR4InfoMessageType.DC301);
    if (msg.info instanceof TR4Info) {
      msg.info.userMsgResponse = true;
      msg.info.serialNumber = this.deviceId;
      msg.info.setInfo = true;
      msg.info.configType = mode;
    }
    return msg;
  }

  getRapidCheckInMessage(enabled: boolean): EndDeviceInfoMessage {
    const msg = new EndDeviceInfoMessage(this.deviceId, TR4InfoMessageType.DC301);
    if (msg.info instanceof TR4Info) {
      msg.info.userMsgResponse = true;
      msg.info.serialNumber = this.deviceId;
      if (enabled) {
        msg.info.startRapidCheckIn = true;
      } else {
        msg.info.stopRapidCheckIn = true;
      }
    }
    return msg;
  }

  getUpdateMeterMsg(info: TR4Info): EndDeviceInfoMessage {
    const msg = new EndDeviceInfoMessage(this.deviceId, TR4InfoMessageType.DC301);
    if (msg.info instanceof TR4Info) {
      msg.info.userMsgResponse = true;
      msg.info.serialNumber = this.deviceId;
      msg.info.setInfo = true;
      msg.info.resetPulseCount = info.resetPulseCount;
      msg.info.configType = info.configType;
    }
    return msg;
  }

  getRapidCheckInMsg(enabled: boolean): EndDeviceInfoMessage {
    const msg = new EndDeviceInfoMessage(this.deviceId, TR4InfoMessageType.DC301);
    if (msg.info instanceof TR4Info) {
      msg.info.userMsgResponse = true;
      msg.info.serialNumber = this.deviceId;
      if (enabled) {
        msg.info.startRapidCheckIn = true;
      } else {
        msg.info.stopRapidCheckIn = true;
      }
    }
    return msg;
  }

  getFactoryResetMsg(): EndDeviceInfoMessage {
    const msg = new EndDeviceInfoMessage(this.deviceId, TR4InfoMessageType.DC301);
    if (msg.info instanceof TR4Info) {
      msg.info.userMsgResponse = true;
      msg.info.serialNumber = this.deviceId;
      msg.info.factoryReset = true;
    }
    return msg;
  }

  /**
   * Returns a message to enable or disable the legacy mode of the TR4-I.
   */
  getLegacyModeMsg(enabled: boolean): EndDeviceInfoMessage {
    const msg = new EndDeviceInfoMessage(this.deviceId, TR4InfoMessageType.DC301);
    if (msg.info instanceof TR4Info) {
      msg.info.userMsgResponse = true;
      msg.info.serialNumber = this.deviceId;
      if (enabled) {
        msg.info.enable201Radio = true;
      } else {
        // !This is how you disable the 201 radio, rather than setting enable20Radio to false
        msg.info.disable01Radio = true;
      }
    }
    return msg;
  }

  getResetPulseCountMsg(): EndDeviceInfoMessage {
    const msg = new EndDeviceInfoMessage(this.deviceId, TR4InfoMessageType.DC301);
    if (msg.info instanceof TR4Info) {
      msg.info.userMsgResponse = true;
      msg.info.serialNumber = this.deviceId;
      msg.info.resetPulseCount = true;
    }
    return msg;
  }

  private computeOverallStatus(tr4: TR4): OverallStatus {
    const status: OverallStatus = {
      isOk: true,
      label: '',
      description: '',
    };

    status.isOk = tr4.gatewayReplied && this.signalStrengthStr !== 'Poor';
    status.label =
      this.signalStrengthStr === 'Poor'
        ? 'Poor Transceiver Signal'
        : tr4.gatewayReplied
        ? 'All Good'
        : 'Not Communicating With Gateway';
    status.description = tr4.gatewayReplied
      ? `This device has a${this.signalStrengthStr === 'Excellent' ? 'n' : ''} ${
          this.signalStrengthStr
        } connection to the Gateway.`
      : 'This TR4 has not received a response from any nearby Gateways. This could mean that the device is either not programmed to a Unit yet or it is not within range of a Gateway.';

    return status;
  }
  // BBE00001
}
